/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Dynamic Source Selector Component
 * 
 * Allows users to either select an existing knowledge source or create a new one
 * inline. New sources are validated and automatically created when needed.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useApp } from '@/contexts/AppContext';
import { 
  Database,
  FolderOpen,
  Cloud,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
} from 'lucide-react';

// Services
import {
  KNOWLEDGE_MINING_QUERY_KEYS,
  validateSourceConfig,
  createKnowledgeSource,
  resolveFilesystemPath,
} from '@/services/skillshop';
import type { 
  KnowledgeSource,
  SourceType,
} from '@/services/skillshop';

// ============================================================================
// Types
// ============================================================================

type SelectionMode = 'existing' | 'new';
type NewSourceType = 'filesystem' | 'gcs';

interface ValidationResult {
  success: boolean;
  message: string;
  resolvedPath?: string;
  details?: {
    filesystemInfo?: {
      basePath: string;
      fileCount: number;
      totalSize: number;
      extensions: string[];
    };
  };
}

export interface SelectedSource {
  /** The source ID to use for generation */
  sourceId: string;
  /** The source name for display */
  sourceName: string;
  /** The source type */
  sourceType: SourceType;
  /** Whether this was a newly created source */
  isNew: boolean;
}

interface DynamicSourceSelectorProps {
  /** Available existing sources */
  sources: KnowledgeSource[];
  /** Loading state for sources */
  isLoadingSources: boolean;
  /** Called when a valid source is selected/created */
  onSourceSelected: (source: SelectedSource | null) => void;
  /** Disabled state */
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function DynamicSourceSelector({
  sources,
  isLoadingSources,
  onSourceSelected,
  disabled = false,
}: DynamicSourceSelectorProps) {
  const { toast } = useToast();
  const { activeProduct } = useApp();
  const queryClient = useQueryClient();

  // Selection mode
  const [mode, setMode] = useState<SelectionMode>('existing');
  
  // Existing source selection
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  
  // New source configuration
  const [newSourceType, setNewSourceType] = useState<NewSourceType>('filesystem');
  const [newSourcePath, setNewSourcePath] = useState<string>('');
  const [newSourceName, setNewSourceName] = useState<string>('');
  
  // Validation state
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  
  // Created source tracking
  const [createdSource, setCreatedSource] = useState<KnowledgeSource | null>(null);

  // Get selected existing source details
  const selectedSource = sources.find(s => s.id === selectedSourceId);

  // Reset validation when inputs change
  useEffect(() => {
    setValidationResult(null);
    setCreatedSource(null);
  }, [newSourceType, newSourcePath, mode]);

  // Auto-generate source name from path
  useEffect(() => {
    if (newSourcePath && !newSourceName) {
      // Extract a name from the path
      const parts = newSourcePath.split('/').filter(Boolean);
      const suggestedName = parts.length > 0 ? parts[parts.length - 1] : '';
      if (suggestedName) {
        setNewSourceName(suggestedName);
      }
    }
  }, [newSourcePath, newSourceName]);

  // Auto-validate path after user stops typing (debounced)
  // Also auto-creates the source if validation succeeds and no existing source matches
  useEffect(() => {
    // Only auto-validate in 'new' mode with a non-empty path
    if (mode !== 'new' || !newSourcePath.trim() || disabled || createdSource) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      // Don't auto-validate if already validating or if we have a result
      if (isValidating || validationResult) {
        return;
      }

      setIsValidating(true);

      try {
        if (newSourceType === 'filesystem') {
          // Resolve relative paths
          const resolved = await resolveFilesystemPath(
            newSourcePath.trim(),
            activeProduct?.repositoryUrl ?? undefined,
            activeProduct?.repoPath ?? undefined
          );

          // Validate the resolved path
          const result = await validateSourceConfig({
            type: 'filesystem',
            basePath: resolved.absolutePath,
          });

          const validationResultWithPath = {
            ...result,
            resolvedPath: resolved.absolutePath,
          };
          setValidationResult(validationResultWithPath);

          if (result.success) {
            toast({
              title: 'Path Valid',
              description: result.message,
            });

            // Check if an existing source matches this path
            const matchingSource = sources.find(s => {
              if (s.type === 'filesystem') {
                const config = s.config as { basePath?: string };
                return config.basePath === resolved.absolutePath;
              }
              return false;
            });

            if (matchingSource) {
              // Use existing source
              setCreatedSource(matchingSource);
            } else {
              // Auto-create new source since no existing source matches
              const sourceName = newSourceName.trim() || newSourcePath.trim().split('/').pop() || 'Untitled Source';
              
              try {
                const newSource = await createKnowledgeSource({
                  type: 'filesystem',
                  name: sourceName,
                  config: { basePath: resolved.absolutePath },
                });
                
                queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.sources });
                setCreatedSource(newSource);
              } catch (createError) {
                console.error('[DynamicSourceSelector] Failed to auto-create source:', createError);
                // Show error so user knows something went wrong
                toast({
                  title: 'Setup Failed',
                  description: 'Could not prepare source for generation. Please try again.',
                  variant: 'destructive',
                });
              }
            }
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Validation failed';
        setValidationResult({
          success: false,
          message,
        });
      } finally {
        setIsValidating(false);
      }
    }, 800); // 800ms debounce

    return () => clearTimeout(timeoutId);
  }, [mode, newSourcePath, newSourceType, newSourceName, activeProduct, disabled, createdSource, isValidating, validationResult, sources, queryClient, toast]);

  // Handle mode change
  const handleModeChange = useCallback((newMode: SelectionMode) => {
    setMode(newMode);
    // Reset selections
    setSelectedSourceId('');
    setCreatedSource(null);
    setValidationResult(null);
  }, []);

  // Validate new source path
  const handleValidate = useCallback(async () => {
    if (!newSourcePath.trim()) {
      toast({
        title: 'Path Required',
        description: 'Please enter a path to validate.',
        variant: 'destructive',
      });
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      if (newSourceType === 'filesystem') {
        // Resolve relative paths
        const resolved = await resolveFilesystemPath(
          newSourcePath.trim(),
          activeProduct?.repositoryUrl ?? undefined,
          activeProduct?.repoPath ?? undefined
        );

        // Validate the resolved path
        const result = await validateSourceConfig({
          type: 'filesystem',
          basePath: resolved.absolutePath,
        });

        setValidationResult({
          ...result,
          resolvedPath: resolved.absolutePath,
        });

        if (result.success) {
          toast({
            title: 'Path Valid',
            description: result.message,
          });
        } else {
          toast({
            title: 'Validation Failed',
            description: result.message,
            variant: 'destructive',
          });
        }
      } else if (newSourceType === 'gcs') {
        // GCS validation not yet implemented
        toast({
          title: 'Not Implemented',
          description: 'GCS validation is not yet available. Please use a filesystem source.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Validation failed';
      setValidationResult({
        success: false,
        message,
      });
      toast({
        title: 'Validation Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsValidating(false);
    }
  }, [newSourcePath, newSourceType, activeProduct, toast]);

  // Create source mutation
  const createSourceMutation = useMutation({
    mutationFn: async () => {
      if (!validationResult?.success || !validationResult.resolvedPath) {
        throw new Error('Please validate the path first');
      }

      const sourceName = newSourceName.trim() || newSourcePath.trim().split('/').pop() || 'Untitled Source';

      if (newSourceType === 'filesystem') {
        return createKnowledgeSource({
          type: 'filesystem',
          name: sourceName,
          config: { basePath: validationResult.resolvedPath },
        });
      }

      throw new Error(`Creating ${newSourceType} sources is not yet implemented`);
    },
    onSuccess: (source) => {
      queryClient.invalidateQueries({ queryKey: KNOWLEDGE_MINING_QUERY_KEYS.sources });
      setCreatedSource(source);
      toast({
        title: 'Source Created',
        description: `Knowledge source "${source.name}" has been created and is ready to use.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Creation Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Handle create source
  const handleCreateSource = useCallback(() => {
    createSourceMutation.mutate();
  }, [createSourceMutation]);

  // Ref for the hidden directory input
  const directoryInputRef = useRef<HTMLInputElement>(null);

  // Handle browse button click - opens native OS folder picker
  const handleBrowseFolder = useCallback(async () => {
    // Check if we're in Electron and have access to native dialog
    const electron = (window as any).electron;
    if (electron?.selectDirectory) {
      try {
        // Use Electron's native dialog which returns the full path
        const result = await electron.selectDirectory({
          title: 'Select Documentation Directory',
          buttonLabel: 'Select Folder',
        });
        
        if (!result.canceled && result.path) {
          setNewSourcePath(result.path);
          setNewSourceName(''); // Reset name so it auto-generates from new path
          
          toast({
            title: 'Folder Selected',
            description: `Selected: ${result.path}`,
          });
        }
      } catch (error) {
        console.error('Error opening folder picker:', error);
        toast({
          title: 'Error',
          description: 'Failed to open folder picker. Please enter the path manually.',
          variant: 'destructive',
        });
      }
      return;
    }
    
    // Fallback: Use the hidden directory input for web browsers
    // This triggers the native OS folder picker via webkitdirectory
    directoryInputRef.current?.click();
  }, [toast]);

  // Handle directory selection from the file input
  const handleDirectoryInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Extract the folder name from the first file's webkitRelativePath
    // The path looks like "folder-name/subfolder/file.txt"
    const firstFile = files[0] as File & { webkitRelativePath?: string };
    const relativePath = firstFile.webkitRelativePath || '';
    const folderName = relativePath.split('/')[0];

    if (folderName) {
      setNewSourcePath(folderName);
      setNewSourceName(''); // Reset name so it auto-generates from new path
      
      toast({
        title: 'Folder Selected',
        description: `Selected folder: "${folderName}" (${files.length} files). Enter the full absolute path to this folder.`,
      });
    }

    // Reset the input so the same folder can be selected again
    event.target.value = '';
  }, [toast]);

  // Check if an existing source matches the path (avoid duplicates)
  const existingSourceForPath = sources.find(s => {
    if (s.type === 'filesystem' && validationResult?.resolvedPath) {
      const config = s.config as { basePath?: string };
      return config.basePath === validationResult.resolvedPath;
    }
    return false;
  });

  // Use existing source if one already matches (silently, no user notification needed)
  useEffect(() => {
    if (existingSourceForPath && mode === 'new' && !createdSource) {
      setCreatedSource(existingSourceForPath);
    }
  }, [existingSourceForPath, mode, createdSource]);

  // Notify parent of selection changes
  useEffect(() => {
    if (mode === 'existing' && selectedSource) {
      onSourceSelected({
        sourceId: selectedSource.id,
        sourceName: selectedSource.name,
        sourceType: selectedSource.type,
        isNew: false,
      });
    } else if (mode === 'new' && (createdSource || existingSourceForPath)) {
      // Use createdSource if available, otherwise use the existing source found for the path
      const source = createdSource || existingSourceForPath!;
      onSourceSelected({
        sourceId: source.id,
        sourceName: source.name,
        sourceType: source.type,
        // isNew is false if we're using an existing source found for the path
        isNew: !existingSourceForPath,
      });
    } else {
      onSourceSelected(null);
    }
  }, [mode, selectedSource, createdSource, existingSourceForPath, onSourceSelected]);

  const canValidate = newSourcePath.trim().length > 0;
  const canCreate = validationResult?.success && !createdSource && !existingSourceForPath;
  const isSourceReady = (mode === 'existing' && selectedSource) || 
                        (mode === 'new' && (createdSource || existingSourceForPath));

  return (
    <div className="space-y-4">
      {/* Hidden directory input for folder selection (webkitdirectory) */}
      <input
        ref={directoryInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is a non-standard but widely supported attribute
        webkitdirectory=""
        directory=""
        onChange={handleDirectoryInputChange}
        className="hidden"
        disabled={disabled || !!createdSource}
      />

      {/* Mode Toggle */}
      <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`px-4 ${mode === 'existing' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
          onClick={() => handleModeChange('existing')}
          disabled={disabled}
        >
          <Database className="h-4 w-4 mr-2" />
          Select Source
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`px-4 ${mode === 'new' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
          onClick={() => handleModeChange('new')}
          disabled={disabled}
        >
          <FolderOpen className="h-4 w-4 mr-2" />
          Specify Path
        </Button>
      </div>

      {/* Existing Source Selection */}
      {mode === 'existing' && (
        <div className="space-y-3">
          <Select
            value={selectedSourceId}
            onValueChange={setSelectedSourceId}
            disabled={isLoadingSources || disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={isLoadingSources ? "Loading sources..." : "Select a configured knowledge source..."} />
            </SelectTrigger>
            <SelectContent>
              {sources.length === 0 ? (
                <SelectItem value="__none__" disabled>No sources available</SelectItem>
              ) : (
                sources.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    <div className="flex items-center gap-2">
                      {source.type === 'filesystem' ? (
                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                      ) : source.type === 'gcs' ? (
                        <Cloud className="h-4 w-4 text-muted-foreground" />
                      ) : source.type === 'sharepoint' ? (
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Database className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{source.name}</span>
                      <Badge variant="outline" className="text-xs">{source.type}</Badge>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {selectedSource && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-green-800">
                <CheckCircle className="h-4 w-4" />
                <span className="font-medium">Source selected:</span>
                <span>{selectedSource.name}</span>
              </div>
            </div>
          )}

          {sources.length === 0 && !isLoadingSources && (
            <div className="flex items-center gap-2 p-3 text-sm border rounded-md bg-muted/50">
              <AlertCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">
                No sources available. Switch to "Specify Path" to enter a document directory.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Specify Path Configuration */}
      {mode === 'new' && (
        <div className="space-y-4">
          {/* Path Input - simplified, no source type selector needed */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                value={newSourcePath}
                onChange={(e) => setNewSourcePath(e.target.value)}
                placeholder="/path/to/docs (or relative: docs)"
                disabled={disabled || !!createdSource}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleBrowseFolder}
                disabled={disabled || !!createdSource}
                title="Browse for folder"
              >
                <FolderOpen className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {activeProduct?.repositoryUrl
                ? "Enter an absolute path or a path relative to the product repository."
                : "Enter an absolute path to the directory containing your documents."}
            </p>
          </div>

          {/* Validation/Status feedback */}
          {isValidating && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Validating path...</span>
            </div>
          )}

          {validationResult && !isValidating && (
            <div className={`rounded-lg p-3 text-sm ${
              validationResult.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {validationResult.success ? (
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className="font-medium">
                    {validationResult.success ? 'Path Valid' : 'Invalid Path'}
                  </div>
                  <div className="mt-1">{validationResult.message}</div>
                </div>
              </div>
            </div>
          )}

          {/* Source ready confirmation */}
          {createdSource && (
            <div className="flex items-center gap-2 p-3 text-sm bg-green-50 border border-green-200 rounded-lg text-green-800">
              <CheckCircle className="h-4 w-4" />
              <span>Ready to generate from: <span className="font-medium">{createdSource.name}</span></span>
            </div>
          )}
        </div>
      )}

      {/* Ready indicator */}
      {isSourceReady && (
        <div className="flex items-center gap-2 text-sm text-green-700 pt-2">
          <ArrowRight className="h-4 w-4" />
          <span>Ready! Now enter your playbook topic below.</span>
        </div>
      )}
    </div>
  );
}
